import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Scanner from "./pages/Scanner";
import StockDetail from "./pages/StockDetail";
import Watchlist from "./pages/Watchlist";
import Backtest from "./pages/Backtest";
import Trades from "./pages/Trades";
import Paywall from "./pages/Paywall";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

const Spinner = () => (
  <div className="flex h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
);

// Requires auth + active access (admin / subscriber / trial)
function Protected({ children }) {
  const { user, access } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (access === null) return <Spinner />;
  if (!access.access) return <Navigate to="/paywall" replace />;
  return <Layout>{children}</Layout>;
}

// Requires auth only (paywall + payment result pages)
function AuthedOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "JetBrains Mono", fontSize: "13px" } }} />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/paywall" element={<AuthedOnly><Paywall /></AuthedOnly>} />
          <Route path="/payment/success" element={<AuthedOnly><PaymentSuccess /></AuthedOnly>} />
          <Route path="/payment/cancel" element={<AuthedOnly><PaymentCancel /></AuthedOnly>} />
          <Route path="/" element={<Protected><Scanner /></Protected>} />
          <Route path="/stock/:ticker" element={<Protected><StockDetail /></Protected>} />
          <Route path="/watchlist" element={<Protected><Watchlist /></Protected>} />
          <Route path="/backtest" element={<Protected><Backtest /></Protected>} />
          <Route path="/trades" element={<Protected><Trades /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
